import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../styles/global.css';

interface FAQItem {
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    question: 'How do I invite my child?',
    answer: 'Go to your league, enter their email address, and click "Send Invite." They\'ll get an email with a link to join. You can also copy the invite link and send it to them directly.',
  },
  {
    question: 'What are flash card sets?',
    answer: 'Flash card sets are collections of multiple-choice questions grouped by topic and difficulty. Your child works through them in order, earning XP and coins for correct answers.',
  },
  {
    question: 'Can I create sets for non-math topics?',
    answer: 'Yes! When you create a custom flash card set, just describe any topic and AI will generate 20 multiple-choice questions. It works for science, history, vocabulary, and more.',
  },
  {
    question: 'How does leveling up work?',
    answer: 'Your child earns XP by answering questions correctly. When they earn enough XP, their character levels up and their avatar evolves with a brand-new AI-generated look!',
  },
  {
    question: 'Is this free?',
    answer: 'Yes, completely free.',
  },
  {
    question: 'How do I change what my child practices?',
    answer: 'Go to your league, click their name in the leaderboard, and you\'ll see their flash card sets. You can add, remove, or drag to reorder the sets.',
  },
  {
    question: 'My child\'s flash card set says "locked"',
    answer: 'Flash card sets unlock in order. Your child needs to complete earlier sets first before the next ones open up. This keeps them progressing at a comfortable pace.',
  },
];

const ManagerHelp: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const firstVisit = (location.state as any)?.firstVisit === true;
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="page">
      <nav className="navbar">
        <div className="nav-brand">
          {!firstVisit && (
            <button
              className="link-btn"
              onClick={() => navigate('/manager/dashboard')}
              style={{ marginRight: '10px' }}
            >
              &larr; Back
            </button>
          )}
          Help
        </div>
        {firstVisit && (
          <div className="nav-links">
            <button className="nav-btn" onClick={() => navigate('/manager/dashboard')}>
              I'm ready to go!
            </button>
          </div>
        )}
      </nav>

      <div className="container" style={{ maxWidth: '700px' }}>
        {/* Hero / Intro */}
        <div className="card" style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '12px', color: 'var(--primary-color)' }}>
            Welcome to BlockMath!
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', lineHeight: '1.7' }}>
            Here's everything you need to know. Your kids practice math (and more) through flash card games, earn XP and coins, level up their characters, and compete on leaderboards.
          </p>
        </div>

        {/* How It Works (for kids) */}
        <div className="card">
          <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: 'var(--text-color)' }}>
            How It Works (for kids)
          </h2>
          <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ fontSize: '1.3rem', lineHeight: '1.4', flexShrink: 0 }}>1.</span>
              <span><strong>Create a character</strong> — Pick a character and give it a name</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ fontSize: '1.3rem', lineHeight: '1.4', flexShrink: 0 }}>2.</span>
              <span><strong>Play flash card sets</strong> — Answer questions to earn XP and coins</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ fontSize: '1.3rem', lineHeight: '1.4', flexShrink: 0 }}>3.</span>
              <span><strong>Level up</strong> — XP unlocks new levels, and each level evolves their character's avatar</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ fontSize: '1.3rem', lineHeight: '1.4', flexShrink: 0 }}>4.</span>
              <span><strong>Visit the store</strong> — Spend coins on new characters</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ fontSize: '1.3rem', lineHeight: '1.4', flexShrink: 0 }}>5.</span>
              <span><strong>Compete</strong> — See their rank on the league leaderboard</span>
            </li>
          </ol>
        </div>

        {/* How to Manage (for you) */}
        <div className="card">
          <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: 'var(--text-color)' }}>
            How to Manage (for you)
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary-color)', fontWeight: 'bold', flexShrink: 0 }}>&bull;</span>
              <span><strong>Create a league</strong> — Your group of players (a class, family, tutoring group)</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary-color)', fontWeight: 'bold', flexShrink: 0 }}>&bull;</span>
              <span><strong>Invite players</strong> — Send an email invite or copy the link</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary-color)', fontWeight: 'bold', flexShrink: 0 }}>&bull;</span>
              <span><strong>View the leaderboard</strong> — See who's been practicing</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary-color)', fontWeight: 'bold', flexShrink: 0 }}>&bull;</span>
              <span><strong>Customize flash card sets</strong> — Assign specific sets per player, reorder the progression, or create custom sets on any topic</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary-color)', fontWeight: 'bold', flexShrink: 0 }}>&bull;</span>
              <span><strong>Create custom flash card sets</strong> — Describe a topic and AI generates 20 multiple-choice questions</span>
            </li>
          </ul>
        </div>

        {/* Tips for Parents */}
        <div className="card">
          <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: 'var(--text-color)' }}>
            Tips for Parents
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ flexShrink: 0 }}>&#10003;</span>
              <span>Start with the default sets — they're ordered by difficulty</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ flexShrink: 0 }}>&#10003;</span>
              <span>Check the leaderboard weekly to see who's active</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ flexShrink: 0 }}>&#10003;</span>
              <span>Create custom sets for topics your kid is struggling with</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ flexShrink: 0 }}>&#10003;</span>
              <span>Reorder a player's sets to focus on what matters most</span>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ flexShrink: 0 }}>&#10003;</span>
              <span>Delete, add, or rearrange flash card sets to match your kid's skill level</span>
            </li>
          </ul>
        </div>

        {/* FAQ */}
        <div className="card" style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: 'var(--text-color)' }}>
            Frequently Asked Questions
          </h2>
          <div>
            {faqItems.map((item, index) => (
              <div
                key={index}
                style={{
                  borderBottom: index < faqItems.length - 1 ? '1px solid var(--border-color)' : 'none',
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleFAQ(index)}
                  aria-expanded={openFAQ === index}
                  aria-controls={`faq-answer-${index}`}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 0',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    color: 'var(--text-color)',
                  }}
                >
                  <span>{item.question}</span>
                  <span aria-hidden="true" style={{ marginLeft: '12px', flexShrink: 0, color: 'var(--text-secondary)' }}>
                    {openFAQ === index ? '\u25BE' : '\u25B8'}
                  </span>
                </button>
                {openFAQ === index && (
                  <div
                    id={`faq-answer-${index}`}
                    role="region"
                    style={{
                      padding: '0 0 14px 0',
                      color: 'var(--text-secondary)',
                      fontSize: '0.9rem',
                      lineHeight: '1.6',
                    }}
                  >
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerHelp;
